/**
 * Activity tab — federation audit log, newest first.
 *
 * Server-side filters: partner (idp_name) and event type. Offset
 * pagination without a total count — "next" is enabled while a full
 * page came back.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  listAuditEvents,
  type ListAuditEventsFilters,
} from '@optum/api-client/endpoints/federation';
import { listFederatedPartners } from '@optum/api-client/endpoints/federated-partners';

import { Button } from '../../ui/Button.js';
import { ListToolbar } from '../../ui/ListToolbar.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { Combobox } from '../../ui/Combobox.js';
import { DataTable } from '../../ui/DataTable.js';
import { Select } from '../../ui/Select.js';

const PAGE_SIZE = 50;
const ALL = 'all';

const EVENT_TYPES = [
  'partner.registered',
  'partner.created',
  'partner.approved',
  'partner.rejected',
  'partner.rotated',
  'partner.revoked',
  'partner.deleted',
  'org.created',
  'membership.granted',
  'membership.role_changed',
  'membership.revoked',
  'user.email_corrected',
];

export function ActivityTab() {
  const [idpName, setIdpName] = useState(ALL);
  const [eventType, setEventType] = useState(ALL);
  const [page, setPage] = useState(0);

  const filters: ListAuditEventsFilters = {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    ...(idpName !== ALL ? { idp_name: idpName } : {}),
    ...(eventType !== ALL ? { event_type: eventType } : {}),
  };
  const query = useQuery({
    queryKey: qk.federationAudit({ ...filters }),
    queryFn: () => listAuditEvents(filters),
  });
  const partners = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
  });

  return (
    <>
      <ListToolbar
        filters={
          <>
            <div style={{ width: 224 }}>
              <Combobox
                aria-label="Partner"
                value={idpName}
                onChange={(v) => {
                  setIdpName(v);
                  setPage(0);
                }}
                searchPlaceholder="Search partners…"
                options={[
                  { value: ALL, label: 'All partners' },
                  ...(partners.data ?? []).map((p) => ({
                    value: p.idp_name,
                    label: p.name,
                    meta: p.idp_name,
                  })),
                ]}
              />
            </div>
            <div style={{ width: 224 }}>
              <Select
                aria-label="Event type"
                value={eventType}
                onChange={(value) => {
                  setEventType(value);
                  setPage(0);
                }}
                options={[
                  { value: ALL, label: 'All event types' },
                  ...EVENT_TYPES.map((t) => ({ value: t, label: t })),
                ]}
              />
            </div>
          </>
        }
      />

      {query.isPending && <LoadingBlock />}
      {query.isError && (
        <ErrorBlock
          message={query.error instanceof Error ? query.error.message : 'Failed to load'}
        />
      )}
      {query.data?.length === 0 && (
        <EmptyBlock>
          {page === 0 ? 'No federation activity recorded yet.' : 'No more events.'}
        </EmptyBlock>
      )}
      {query.data && query.data.length > 0 && (
        <DataTable
          rows={query.data}
          columns={[
            {
              key: 'created_at',
              label: 'Time',
              mono: true,
              render: (e) => <span title={e.created_at}>{absoluteDateTime(e.created_at)}</span>,
            },
            { key: 'idp_name', label: 'Partner (idp_name)', mono: true },
            { key: 'event_type', label: 'Event', strong: true },
            {
              key: 'external_sub',
              label: 'Subject',
              mono: true,
              render: (e) => e.external_sub ?? '—',
            },
            {
              key: 'detail',
              label: 'Detail',
              wrap: true,
              render: (e) =>
                e.detail && Object.keys(e.detail).length > 0 ? (
                  <details>
                    <summary
                      style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
                    >
                      detail
                    </summary>
                    <pre
                      style={{
                        marginTop: 5,
                        maxWidth: '420px',
                        overflowX: 'auto',
                        borderRadius: 'var(--radius-chip)',
                        background: 'var(--surface-fill)',
                        padding: 7,
                        fontSize: 'var(--text-meta-lg)',
                      }}
                    >
                      {JSON.stringify(e.detail, null, 2)}
                    </pre>
                  </details>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 7,
          borderTop: '1px solid var(--border-hairline)',
          borderColor: 'var(--border-hairline)',
          padding: 11,
        }}
      >
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0 || query.isPending}
        >
          ← Newer
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setPage((p) => p + 1)}
          disabled={query.isPending || (query.data?.length ?? 0) < PAGE_SIZE}
        >
          Older →
        </Button>
      </div>
    </>
  );
}
